import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../context/AppContext";
import { useBrand } from "../context/BrandContext";
import PublicCatalogView from "./PublicCatalogView";
import api, { fmtDate, STATUS_LABELS, STATUS_STYLES } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "../components/ui/sheet";
import { ShoppingCart, Plus, Minus, Trash2, Search, MapPin, Package, ChevronRight, Building2, FileText, Truck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { OrderProgress } from "../components/OrderProgress";
import { ClientInvoices } from "../components/InvoicesManager";

const HERO = "https://images.unsplash.com/photo-1756362847925-71c792d6729c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const paletteUnit = (u) => (u || "").replace(/broche/gi, "palette");

function ProductCard({ p, index, inCart, cartSauce, onAdd, onDec, onSetSauce }) {
  const { sauces } = useBrand();
  const [sauce, setSauce] = useState(cartSauce || sauces[0] || "");
  const changeSauce = (v) => {
    setSauce(v);
    if (inCart > 0) onSetSauce(p, v);
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }}
    >
      <Card data-testid={`product-card-${p.id}`} className="group flex h-full flex-col overflow-hidden transition-all hover:-translate-y-1 hover:shadow-md">
        <div className="relative h-36 overflow-hidden bg-muted">
          {p.image_url ? (
            <img src={p.image_url} alt={p.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-muted-foreground/40"><Package className="h-10 w-10" /></div>
          )}
          <Badge className="absolute left-2 top-2 bg-background/90 text-foreground hover:bg-background/90">{p.category}</Badge>
          <span className={`absolute bottom-2 right-2 rounded-full px-2 py-0.5 text-[11px] font-semibold backdrop-blur ${p.stock < 50 ? "bg-secondary/90 text-secondary-foreground" : "bg-primary/90 text-primary-foreground"}`}>
            {p.stock < 50 ? "Stock faible" : "En stock"} · {p.stock}
          </span>
        </div>
        <div className="flex flex-1 flex-col p-4">
          <p className="font-medium leading-tight">{p.name}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{paletteUnit(p.unit)}</p>
          {p.conditionnement && <p className="text-xs text-muted-foreground">Cond. : {p.conditionnement}</p>}
          {p.certifications && (
            <div className="mt-1 flex flex-wrap gap-1" data-testid={`certs-${p.id}`}>
              {p.certifications.split(",").map((cx) => cx.trim()).filter(Boolean).map((cx) => (
                <Badge key={cx} variant="outline" className="border-primary/40 bg-primary/10 text-primary text-[10px]">{cx}</Badge>
              ))}
            </div>
          )}
          {p.spec_sheet_url && (
            <a href={p.spec_sheet_url} target="_blank" rel="noreferrer" data-testid={`spec-${p.id}`} className="mt-1 inline-flex w-fit items-center gap-1 text-xs font-medium text-primary hover:underline">
              <FileText className="h-3 w-3" /> Fiche technique
            </a>
          )}
          {sauces.length > 0 && (
            <div className="mt-3">
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Sauce</label>
              <Select value={sauce} onValueChange={changeSauce}>
                <SelectTrigger className="h-9" data-testid={`sauce-select-${p.id}`}>
                  <SelectValue placeholder="Choisir une sauce" />
                </SelectTrigger>
                <SelectContent>
                  {sauces.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`sauce-option-${p.id}-${s}`}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="mt-3 flex items-center justify-end">
            {inCart > 0 ? (
              <div className="flex items-center gap-2">
                <Button size="icon" variant="outline" className="h-8 w-8" data-testid={`dec-${p.id}`} onClick={() => onDec(p)}>
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="tabular w-6 text-center font-semibold">{inCart}</span>
                <Button size="icon" className="h-8 w-8" data-testid={`inc-${p.id}`} onClick={() => onAdd(p, 1, sauce)}>
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <Button size="sm" data-testid={`add-${p.id}`} onClick={() => onAdd(p, 1, sauce)}>
                <Plus className="mr-1 h-4 w-4" /> Ajouter
              </Button>
            )}
          </div>
        </div>
      </Card>
    </motion.div>
  );
}

export default function ClientView() {
  const brand = useBrand();
  const { clientId, setClientId, enseigneName, loginEnseigne, logoutEnseigne, cart, addToCart, setQuantity, setSauce, removeFromCart, clearCart, cartCount } = useApp();
  const sauces = brand.sauces;
  const [depots, setDepots] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("Tous");
  const [notes, setNotes] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [loginName, setLoginName] = useState("");
  const [loginPin, setLoginPin] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const client = depots.find((c) => c.id === clientId);

  useEffect(() => {
    if (enseigneName) {
      api.get("/clients").then((r) => setDepots(r.data)).catch(() => {});
    } else {
      setDepots([]);
    }
  }, [enseigneName]);

  // Le prix (tarif negocie) depend du depot choisi : on recharge le catalogue a chaque changement de depot.
  useEffect(() => {
    if (!enseigneName) { setProducts([]); return; }
    const params = clientId ? { client_id: clientId } : {};
    api.get("/products", { params }).then((r) => setProducts(r.data)).catch(() => {});
  }, [enseigneName, clientId]);

  const loadOrders = () => {
    if (clientId) api.get(`/orders?client_id=${clientId}`).then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(loadOrders, [clientId]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      await loginEnseigne(loginName.trim(), loginPin);
      setLoginName("");
      setLoginPin("");
    } catch {
      setLoginError("Nom ou mot de passe incorrect");
    } finally {
      setLoggingIn(false);
    }
  };

  const categories = useMemo(() => ["Tous", ...Array.from(new Set(products.map((p) => p.category)))], [products]);

  // Tous les hooks ci-dessus sont appeles inconditionnellement (regle des hooks React) ; leur resultat
  // est simplement ignore ici puisqu'on bascule vers un tout autre rendu (aucun compte requis).
  if (brand.public_catalog) return <PublicCatalogView />;

  const filtered = products.filter(
    (p) =>
      (category === "Tous" || p.category === category) &&
      p.name.toLowerCase().includes(query.toLowerCase())
  );

  const requestCancel = async (o) => {
    try {
      await api.patch(`/orders/${o.id}/cancel-request`);
      toast.success("Demande d'annulation envoyée");
      loadOrders();
    } catch {
      toast.error("Impossible d'envoyer la demande");
    }
  };

  const reorderFrom = (o) => {
    const byId = Object.fromEntries(products.map((p) => [p.id, p]));
    const found = o.items.filter((it) => byId[it.product_id] && it.quantity > 0);
    if (!found.length) { toast.error("Ces produits ne sont plus au catalogue"); return; }
    clearCart();
    found.forEach((it) => addToCart(byId[it.product_id], it.quantity, it.sauce && it.sauce !== "—" ? it.sauce : undefined));
    setCartOpen(true);
    const missing = o.items.length - found.length;
    toast.success(`Commande ${o.order_number} ajoutée au panier`, {
      description: missing ? `${missing} produit(s) ne sont plus au catalogue.` : "Vérifiez les quantités puis validez.",
    });
  };

  const currentOrder = orders.find((o) => !["livree", "annulee", "refusee"].includes(o.status)) || orders[0];

  const placeOrder = async () => {
    const items = Object.values(cart).map((i) => ({ product_id: i.product.id, quantity: i.quantity, sauce: i.sauce || sauces[0] || "" }));
    if (!items.length) return;
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", { client_id: clientId, items, notes });
      toast.success(`Commande ${data.order_number} envoyée à l'usine`, {
        description: "Un email de confirmation vous a été adressé.",
      });
      clearCart();
      setNotes("");
      setCartOpen(false);
      loadOrders();
    } catch (e) {
      toast.error("Erreur lors de l'envoi de la commande");
    } finally {
      setPlacing(false);
    }
  };

  // Client login: blank page, name + password only (no list of other clients)
  if (!enseigneName) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center" data-testid="client-login">
        <Card className="w-full max-w-sm p-7">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Building2 className="h-6 w-6" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Espace client</p>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Connexion</h1>
          <p className="mt-1 text-sm text-muted-foreground">Accédez à vos commandes avec votre nom et votre mot de passe.</p>
          <form onSubmit={submitLogin} className="mt-5 space-y-3">
            <div>
              <Label htmlFor="login-name">Nom</Label>
              <Input id="login-name" data-testid="client-name-input" value={loginName} onChange={(e) => setLoginName(e.target.value)} autoFocus placeholder="Votre nom d'accès" />
            </div>
            <div>
              <Label htmlFor="login-pin">Mot de passe</Label>
              <Input id="login-pin" data-testid="client-pin-input" type="password" value={loginPin} onChange={(e) => setLoginPin(e.target.value)} placeholder="••••" />
            </div>
            {loginError && <p className="text-sm text-destructive" data-testid="client-login-error">{loginError}</p>}
            <Button type="submit" className="w-full" size="lg" disabled={loggingIn || !loginName || !loginPin} data-testid="client-login-btn">
              {loggingIn ? "Connexion..." : "Se connecter"}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Step 2: choose dépôt within the enseigne
  if (!client) {
    return (
      <div data-testid="client-selector">
        <button data-testid="back-to-enseigne" onClick={() => logoutEnseigne()} className="mb-3 text-sm font-medium text-primary hover:underline">
          ← Se déconnecter
        </button>
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{enseigneName}</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Sélectionnez votre dépôt</h1>
          <p className="mt-2 max-w-xl text-muted-foreground">Chaque dépôt gère ses commandes et sa comptabilité de façon indépendante.</p>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {depots.map((c) => (
            <button
              key={c.id}
              data-testid={`select-client-${c.id}`}
              onClick={() => setClientId(c.id)}
              className="group flex items-center gap-4 rounded-lg border border-border bg-card p-5 text-left transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
            >
              <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10 font-display text-base font-extrabold text-primary">
                {c.name.split(" ").filter((w) => w && w !== "—").slice(0, 2).map((w) => w[0]).join("")}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-display text-base font-bold leading-tight">{c.name}</p>
                <p className="mt-0.5 flex items-center gap-1 text-sm text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {c.city || "Adresse à renseigner"}
                </p>
              </div>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="client-catalog">
      {/* Hero */}
      <div className="relative mb-6 overflow-hidden rounded-xl border border-border">
        <img src={brand.hero_url || HERO} alt="Entrepôt" className="h-40 w-full object-cover sm:h-52" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" />
        <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-8">
          <p className="text-xs uppercase tracking-[0.2em] text-white/70">Bienvenue</p>
          <h1 className="font-display text-2xl font-extrabold text-white sm:text-3xl">{client.name}</h1>
          <p className="text-sm text-white/80">{client.enseigne} · {client.city || "Adresse à renseigner"}</p>
        </div>
        <button
          data-testid="change-client-btn"
          onClick={() => setClientId(null)}
          className="absolute right-4 top-4 rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/25"
        >
          Changer de dépôt
        </button>
      </div>

      {/* Toolbar */}
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="product-search"
            placeholder="Rechercher un produit..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {categories.map((c) => (
            <button
              key={c}
              data-testid={`category-${c}`}
              onClick={() => setCategory(c)}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                category === c ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Product grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((p, i) => (
          <ProductCard
            key={p.id}
            p={p}
            index={i}
            inCart={cart[p.id]?.quantity || 0}
            cartSauce={cart[p.id]?.sauce}
            onAdd={addToCart}
            onDec={(prod) => addToCart(prod, -1)}
            onSetSauce={setSauce}
          />
        ))}
      </div>

      {/* Current order tracker */}
      {currentOrder && (
        <div className="mt-12" data-testid="current-order-tracker">
          <h2 className="font-display text-xl font-bold">Suivi de votre commande</h2>
          <Card className="mt-4 p-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="tabular font-display text-lg font-bold">{currentOrder.order_number}</p>
                <p className="text-sm text-muted-foreground">{currentOrder.items.length} article(s)</p>
              </div>
              {currentOrder.status === "en_livraison" ? (
                <div className="flex items-center gap-2 rounded-full bg-secondary/15 px-3.5 py-1.5 text-secondary" data-testid="delivery-truck-badge">
                  <Truck className="h-4 w-4" />
                  <span className="text-sm font-semibold">En cours de livraison</span>
                </div>
              ) : (
                <Badge variant="outline" className={STATUS_STYLES[currentOrder.status]}>{STATUS_LABELS[currentOrder.status]}</Badge>
              )}
            </div>
            <OrderProgress status={currentOrder.status} />
            {currentOrder.delivery_date && (
              <p className="mt-6 text-sm text-muted-foreground">
                Livraison prévue le <span className="font-medium text-foreground">{fmtDate(currentOrder.delivery_date)}</span>
              </p>
            )}
          </Card>
        </div>
      )}

      {/* Order history */}
      <div className="mt-12">
        <h2 className="font-display text-xl font-bold">Mes commandes</h2>
        <div className="mt-4 space-y-2">
          {orders.length === 0 && <p className="text-sm text-muted-foreground">Aucune commande pour l'instant.</p>}
          {orders.map((o) => (
            <div key={o.id} data-testid={`client-order-${o.id}`} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted">
                    <Package className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="tabular font-semibold">{o.order_number}</p>
                    <p className="text-xs text-muted-foreground">{o.items.length} article(s) · {fmtDate(o.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => reorderFrom(o)} data-testid={`reorder-${o.id}`}>
                    <RotateCcw className="h-3.5 w-3.5" /> Recommander
                  </Button>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Livraison</p>
                    <p className="text-sm font-medium">{fmtDate(o.delivery_date)}</p>
                  </div>
                  <Badge variant="outline" className={STATUS_STYLES[o.status]}>{STATUS_LABELS[o.status]}</Badge>
                </div>
              </div>
              {o.status !== "annulee" && (
                <div className="mt-4 border-t border-border pt-4">
                  <OrderProgress status={o.status} compact />
                </div>
              )}
              {o.status === "refusee" && o.refus_motif && (
                <p className="mt-3 rounded-md bg-destructive/5 px-3 py-2 text-xs font-medium text-destructive" data-testid={`refus-motif-${o.id}`}>
                  Motif du refus : {o.refus_motif}
                </p>
              )}
              {!["livree", "annulee", "refusee", "en_attente"].includes(o.status) && (
                <div className="mt-3 flex justify-end">
                  {o.cancel_requested ? (
                    <span className="text-xs font-medium text-secondary" data-testid={`cancel-requested-${o.id}`}>
                      Annulation demandée — en attente de validation
                    </span>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-7 text-destructive hover:text-destructive" onClick={() => requestCancel(o)} data-testid={`request-cancel-${o.id}`}>
                      Demander l'annulation
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <ClientInvoices />

      {/* Floating cart */}
      <Sheet open={cartOpen} onOpenChange={setCartOpen}>
        <SheetTrigger asChild>
          <Button
            data-testid="open-cart-btn"
            className="fixed bottom-6 right-6 z-40 h-14 rounded-full px-6 shadow-lg"
            size="lg"
          >
            <ShoppingCart className="mr-2 h-5 w-5" />
            Panier
            {cartCount > 0 && (
              <span className="ml-2 rounded-full bg-secondary px-2 py-0.5 text-xs font-bold text-secondary-foreground">{cartCount}</span>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent className="flex w-full flex-col sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-display">Panier — {client.name}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-3 overflow-y-auto py-4">
            {cartCount === 0 && <p className="text-sm text-muted-foreground">Votre panier est vide.</p>}
            {Object.values(cart).map(({ product, quantity, sauce }) => (
              <div key={product.id} data-testid={`cart-item-${product.id}`} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-3">
                  <img src={product.image_url} alt="" className="h-12 w-12 rounded object-cover" />
                  <div className="flex-1">
                    <p className="text-sm font-medium leading-tight">{product.name}</p>
                    <p className="text-xs text-muted-foreground">{paletteUnit(product.unit)}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQuantity(product, quantity - 1)}>
                      <Minus className="h-3.5 w-3.5" />
                    </Button>
                    <span className="tabular w-5 text-center text-sm font-semibold">{quantity}</span>
                    <Button size="icon" variant="outline" className="h-7 w-7" onClick={() => setQuantity(product, quantity + 1)}>
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => removeFromCart(product.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                {sauces.length > 0 && (
                  <div className="mt-2.5 flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Sauce :</span>
                    <span className="font-medium text-foreground" data-testid={`cart-sauce-${product.id}`}>{sauce || sauces[0]}</span>
                  </div>
                )}
              </div>
            ))}
            {cartCount > 0 && (
              <Textarea
                data-testid="order-notes"
                placeholder="Note pour l'usine (optionnel)..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-2"
              />
            )}
          </div>
          <SheetFooter className="flex-col gap-3 sm:flex-col">
            <Button
              data-testid="place-order-btn"
              className="w-full"
              size="lg"
              disabled={cartCount === 0 || placing}
              onClick={placeOrder}
            >
              {placing ? "Envoi..." : "Valider la commande"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
